import { getAuthHeaders } from "./authUtils";
import {
  emitSessionExpired,
  isSessionExpiredError,
} from "../contexts/SessionExpiredContext";

interface TrackDownloadParams {
  fileName: string;
  fileUrl: string;
  fileId?: string;
  clientId?: string;
}

interface TrackBulkDownloadParams {
  clientId: string;
  fileCount?: number;
  zipUrl: string;
}

/**
 * Gets the base API URL, handling local development environment
 */
export const getApiBaseUrl = (): string => {
  // Check if we're in local development
  if (
    window.location.hostname === "localhost" &&
    window.location.port === "5173"
  ) {
    // Frontend runs on port 5173, backend on configurable port in development
    const backendPort = import.meta.env.VITE_BACKEND_PORT || "4000";
    return `http://localhost:${backendPort}`;
  }

  // In production, use relative URL
  return "";
};

/**
 * Tracks a file download without proxying through the backend
 *
 * @param params Download tracking parameters
 * @returns Promise that resolves when the tracking is complete
 */
export const trackDownload = async (
  params: TrackDownloadParams,
): Promise<void> => {
  const { fileName, fileUrl, fileId, clientId } = params;

  console.log(`Tracking download for file: ${fileName}`, {
    fileName,
    fileUrl,
    fileId,
    clientId,
  });

  try {
    // Get the base URL for the API endpoint
    const baseUrl = getApiBaseUrl();
    const apiUrl = `${baseUrl}/track-download`;

    console.log(`Sending tracking request to: ${apiUrl}`);

    // Track the download via API call
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      body: JSON.stringify({
        fileName,
        fileId,
        clientId,
        url: fileUrl,
      }),
      credentials: "include", // Include cookies if any
    });

    if (!response.ok) {
      // Only 401 indicates session expiry for download routes.
      // 403 from download routes means "not authorized to download these files"
      // (permission error), not session expiry.
      if (response.status === 401) {
        emitSessionExpired();
        return;
      }
      const errorText = await response.text();
      console.error("Failed to track download:", errorText);
      console.warn("Failed to track download:", errorText);
    } else {
      console.log(`Successfully tracked download for: ${fileName}`);
    }
  } catch (error) {
    console.error("Error tracking download:", error);
    // Continue with download even if tracking fails
  }
};

/**
 * Tracks a bulk download of multiple files
 *
 * @param params Bulk download tracking parameters
 */
export const trackBulkDownload = async (
  params: TrackBulkDownloadParams,
): Promise<void> => {
  const { clientId, fileCount, zipUrl } = params;

  console.log(`Tracking bulk download for clientId: ${clientId}`, {
    clientId,
    fileCount,
    zipUrl,
  });

  try {
    // Get the base URL for the API endpoint
    const baseUrl = getApiBaseUrl();
    const apiUrl = `${baseUrl}/track-download`;

    console.log(`Sending bulk tracking request to: ${apiUrl}`);

    // Track the bulk download via API call
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      body: JSON.stringify({
        fileName: `client_${clientId}_files.zip`,
        clientId,
        fileCount,
        url: zipUrl,
        isBulk: true,
      }),
      credentials: "include", // Include cookies if any
    });

    if (!response.ok) {
      // Only 401 indicates session expiry for download routes.
      // 403 from download routes means "not authorized to download these files"
      // (permission error), not session expiry.
      if (response.status === 401) {
        emitSessionExpired();
        return;
      }
      const errorText = await response.text();
      console.error("Failed to track bulk download:", errorText);
      console.warn("Failed to track bulk download:", errorText);
    } else {
      console.log(
        `Successfully tracked bulk download for clientId: ${clientId}`,
      );
    }
  } catch (error) {
    console.error("Error tracking bulk download:", error);
    // Continue with download even if tracking fails
  }
};

/**
 * Downloads a file directly from its URL and tracks the download
 *
 * @param params Download tracking parameters
 */
export const downloadFile = async (
  params: TrackDownloadParams,
): Promise<void> => {
  const { fileName, fileUrl } = params;

  console.log(`Starting download process for: ${fileName}`, params);

  try {
    // First track the download
    await trackDownload(params);

    // Create a temporary link element to trigger the download
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = fileName;
    link.target = "_blank"; // Open in new tab if browser doesn't support download

    console.log(`Triggering browser download for: ${fileName}`);

    // Trigger the download
    document.body.appendChild(link);
    link.click();

    // Clean up
    setTimeout(() => {
      document.body.removeChild(link);
      console.log(`Download process completed for: ${fileName}`);
    }, 100);
  } catch (error) {
    console.error("Error downloading file:", error);
    if (!isSessionExpiredError()) {
      alert("Failed to download file. Please try again.");
    }
  }
};
